import {Component} from 'react';
import {omit} from 'lodash';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import {Button, Item, Label} from 'semantic-ui-react';
import resizedDasWebUri, {modes, presetDimensions} from 'components/imaging/ResizedDasWebImage';
import NavigationIcon from 'components/NavigationIcon';
import 'components/InlineGallery.scss';
import binIcon from 'images/bin.svg';
import PlaceholderImage from 'images/no-image@2x.png';
import ImageLoader from 'components/ImageLoader/ImageLoader';
import Spinner from 'components/Spinner';

export default class InlineGallery extends Component {

    static propTypes = {
        fetched: PropTypes.bool.isRequired,
        currentIndex: PropTypes.number.isRequired,
        images: PropTypes.arrayOf(PropTypes.string).isRequired,
        deletableImages: PropTypes.arrayOf(PropTypes.string.isRequired),
        onIndexChange: PropTypes.func.isRequired,
        onUpload: PropTypes.func,
        onDelete: PropTypes.func,
        imageProps: PropTypes.object,
        resizeProps: PropTypes.shape({
            isResize: PropTypes.bool,
            resizeDimensions: PropTypes.shape({
                width: PropTypes.number,
                height: PropTypes.number,
            }),
        }),
    };

    static defaultProps = {
        images: [],
    };

    constructor(props) {
        super(props);
        this.state = {
            imageSrc: PlaceholderImage,
        };
    }

    getInlineImageSrc() {
        const {images, currentIndex, resizeProps} = this.props;
        let selectedImage = images[currentIndex];
        if (resizeProps?.isResize) {
            selectedImage = resizedDasWebUri({
                uri: selectedImage,
                mode: modes.EXACT,
                resizeDimensions: resizeProps?.resizeDimensions || presetDimensions.MEDIUM,
            });
        }
        return selectedImage;
    }

    resizeDimensions = () => {
        const {resizeProps} = this.props;
        return resizeProps?.isResize ? resizeProps.resizeDimensions : presetDimensions.MEDIUM;
    };

    resetLoaded = () => {
        this.setState({loaded: false});
    };

    next = () => {
        const {images, onUpload} = this.props;
        const next = this.props.currentIndex + 1;
        this.resetLoaded();
        this.props.onIndexChange(next >= images.length + (onUpload ? 1 : 0) ? 0 : next);
    };

    previous = () => {
        const {images, onUpload} = this.props;
        const next = this.props.currentIndex - 1;
        this.resetLoaded();
        this.props.onIndexChange(next < 0 ? images.length - (onUpload ? 0 : 1) : next);
    };

    delete = (e) => {
        const {onDelete, currentIndex} = this.props;
        e.stopPropagation();
        onDelete(currentIndex);
    };

    showNav = () => {
        const {images, fetched, currentIndex} = this.props;
        return [
            (images.length === 0 && fetched),
            (images.length > 0 && fetched && currentIndex >= images.length),
            (images.length > 0 && fetched && currentIndex < images.length),
        ].indexOf(true) !== -1;
    };

	showDropzone = () => {
	  const {images, fetched, currentIndex} = this.props;
	  return [
	      (images.length === 0 && fetched),
	      (images.length > 0 && fetched && currentIndex >= images.length),
      ].indexOf(true) !== -1;
	};

    handleImageError = (e) => {
      e.target.onerror = null;
      e.target.src = PlaceholderImage;
    };

    render() {
        const {
            fetched,
            currentIndex,
            images,
            imageProps = {},
            onUpload,
            deletableImages,
            ...rest
        } = this.props;
        const passThruProps = omit(rest, ['resizeProps', 'onIndexChange', 'onDelete']);
        const resizeDimensions = this.resizeDimensions();

        const imageStyle = {
            ...(passThruProps?.style || {}),
            width: resizeDimensions?.width || 228,
            height: resizeDimensions?.height || 152,
        };

        const imageSrc = this.getInlineImageSrc();

        return (
            <div className={`inline-gallery${fetched ? '' : ' loading'}`} data-testid="InlineGallery">

                {this.showNav() &&
                    <NavigationIcon displayed={images.length + (onUpload ? 1 : 0) > 1}
                                    name="angle left"
                                    size="large"
                                    className="previous"
                                    style={{
                                        left: '0%',
                                        top: '40%',
                                        marginLeft: '0.2em',
                                        paddingRight: '0.1em',
                                        color: 'white'
                                    }}
                                    data-testid="inline-gallery-left-arrow"
                                    onClick={this.previous}/>
                }

                {currentIndex < images.length &&
                    <Item.Image bordered disabled={!fetched} wrapped
                                size="medium"
                                {...imageProps}
                                {...passThruProps}
                                style={{...imageStyle}}
                    >
                        <ImageLoader
                            alt="inline gallery"
                            className="inlineGalleryImg ui fluid image"
                            src={imageSrc}
                            style={{
                                display: 'block',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                                width: 'auto',
                                height: 'inherit',
                            }}
                        />

                        {deletableImages && deletableImages.indexOf(images[currentIndex]) !== -1 && (
                            <img alt="Delete" src={binIcon} className="bin" onClick={this.delete}/>
                        )}

                        {fetched &&
                            <Label className="index" color="black"
                                   style={{
                                       zIndex: 1,
                                       position: 'absolute',
                                       padding: '0.6em',
                                       paddingBottom: '0.607em',
                                       paddingTop: '0.607em',
                                       borderTopRightRadius: '0',
                                       top: 0,
                                       right: 0
                                   }}>
                                {`${currentIndex + 1} / ${images.length}`}
                            </Label>
                        }
                    </Item.Image>
                }
                <Spinner hide={fetched} dimensions={resizeDimensions}/>

                {this.showDropzone() && fetched &&
                    <Dropzone onDropAccepted={onUpload}>
                        {({getRootProps, getInputProps}) => (
                            <div className="upload"
                                 data-testid="dropzone-upload" {...getRootProps()}>
                                <label htmlFor="inline-upload">Add a property image</label>
                                <Button primary>Upload Image</Button>
                                <input id="inline-upload" {...getInputProps()}/>
                            </div>
                        )}
                    </Dropzone>
                }

                {this.showNav() &&
                    <NavigationIcon displayed={images.length + (onUpload ? 1 : 0) > 1}
                                    name="angle right"
                                    size="large"
                                    className="next"
                                    style={{
                                        right: '0%',
                                        top: '40%',
                                        marginRight: '0.23em',
                                        paddingLeft: '0.1em',
                                        color: 'white'
                                    }}
                                    data-testid="inline-gallery-right-arrow"
                                    onClick={this.next}/>
                }
            </div>
        );
    }
}

